<template>
    <v-card class="register-card" elevation="0" dark>
        <header class="card-heading">
            <v-icon small>{{ mdiCheckboxMarkedCircleOutline }}</v-icon>
            <h2>Add a device</h2>
        </header>
        <p class="card-description">Register a serial number and add optional asset and location details.</p>

        <v-row dense class="register-fields">
            <v-col v-if="userIsOrgAdmin" cols="12">
                <v-select
                    v-model="registerSerialToOrg"
                    outlined
                    dense
                    hide-details
                    return-object
                    item-text="organizationName"
                    label="Organization (required)"
                    clearable
                    color="#f0784c"
                    :items="orgList"
                    :loading="loading"
                >
                    <template v-slot:prepend-inner>
                        <v-icon small>{{ mdiAccountSupervisorCircle }}</v-icon>
                    </template>
                </v-select>
            </v-col>
            <v-col cols="12">
                <v-text-field
                    v-model="serialNumber"
                    outlined
                    dense
                    label="Serial number (required)"
                    hide-details="auto"
                    color="#f0784c"
                >
                    <template v-slot:prepend-inner>
                        <v-icon small>{{ mdiBarcode }}</v-icon>
                    </template>
                </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
                <v-text-field
                    v-model="assetTag"
                    outlined
                    dense
                    label="Asset tag (optional)"
                    hide-details="auto"
                    color="#f0784c"
                >
                    <template v-slot:prepend-inner>
                        <v-icon small>{{ mdiTag }}</v-icon>
                    </template>
                </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
                <v-text-field
                    v-model="location"
                    outlined
                    dense
                    label="Location (optional)"
                    hide-details="auto"
                    color="#f0784c"
                >
                    <template v-slot:prepend-inner>
                        <v-icon small>{{ mdiMapMarker }}</v-icon>
                    </template>
                </v-text-field>
            </v-col>
        </v-row>

        <div class="register-actions">
            <v-btn
                class="register-submit"
                color="#db5120"
                elevation="0"
                :loading="serialSubmitLoading"
                :disabled="serialSubmitDisabled"
                @click="$emit('submit-serial', { serialNumber, assetTag, location, registerSerialToOrg })"
            >
                <v-icon small left>{{ mdiArrowRightBoldCircle }}</v-icon>
                Register device
            </v-btn>
        </div>

        <section v-if="successfulRegistrations.length" class="register-results" aria-label="Recent registrations">
            <h3
                ><v-icon small>{{ mdiHistory }}</v-icon> Recent successful registrations</h3
            >
            <v-data-table
                class="register-table"
                dark
                dense
                disable-sort
                :mobile-breakpoint="0"
                :headers="successHeaders"
                :items="successfulRegistrations"
            >
                <template v-slot:item.iw="{ item }">{{ item.iw ? 'Yes' : 'No' }}</template>
            </v-data-table>
        </section>
    </v-card>
</template>

<script>
import {
    mdiCheckboxMarkedCircleOutline,
    mdiAccountSupervisorCircle,
    mdiBarcode,
    mdiTag,
    mdiMapMarker,
    mdiArrowRightBoldCircle,
    mdiHistory
} from '@mdi/js'

export default {
    name: 'RegisterSerialTab',
    props: {
        userIsOrgAdmin: { type: Boolean, required: true },
        orgList: { type: Array, default: () => [] },
        serialSubmitLoading: { type: Boolean, default: false },
        successfulRegistrations: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false }
    },
    data() {
        return {
            mdiCheckboxMarkedCircleOutline,
            mdiAccountSupervisorCircle,
            mdiBarcode,
            mdiTag,
            mdiMapMarker,
            mdiArrowRightBoldCircle,
            mdiHistory,
            registerSerialToOrg: '',
            serialNumber: '',
            assetTag: '',
            location: '',
            successHeaders: [
                { text: 'Serial #', value: 'serial', align: 'left' },
                { text: 'Model', value: 'model', align: 'left' },
                { text: 'Organization', value: 'org', align: 'left' },
                { text: 'Asset Tag', value: 'assetTag', align: 'left' },
                { text: 'Location', value: 'location', align: 'left' },
                { text: 'In Warranty', value: 'iw', align: 'left' }
            ]
        }
    },
    computed: {
        serialSubmitDisabled() {
            if (this.serialSubmitLoading || !this.serialNumber || !this.serialNumber.trim()) {
                return true
            }
            if (this.userIsOrgAdmin) {
                return !this.registerSerialToOrg || !this.registerSerialToOrg.orgId
            }
            return false
        }
    }
}
</script>

<style scoped>
.register-card {
    width: 100%;
    min-width: 0;
    height: 100%;
    padding: 20px;
    border: 1px solid #3b3e47;
    border-radius: 12px !important;
    background: #282b33 !important;
    color: #f3f4f6;
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.card-heading,
.register-results h3 {
    display: flex;
    align-items: center;
    gap: 8px;
}
.card-heading h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.3px;
}
.card-heading .v-icon,
.register-results h3 .v-icon {
    color: #f0784c;
}
.card-description {
    margin: 8px 0 16px;
    color: #b8bbc5;
    line-height: 1.6;
}
.register-fields > .col {
    padding-bottom: 8px;
}
.register-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}
.register-card ::v-deep .v-btn {
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}
.register-submit:hover:not(:disabled) {
    background-color: #f0784c !important;
}
.register-card ::v-deep .v-input,
.register-card ::v-deep .v-label {
    font-family: inherit;
    font-size: 12px;
}
.register-card ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
    min-height: 40px;
    background: #22252d;
    border-radius: 8px;
}
.register-card ::v-deep .v-text-field--outlined:not(.v-input--is-focused) fieldset {
    border-color: #484c57;
}
.register-card ::v-deep .v-input .v-icon {
    color: #b8bbc5;
}
.register-results {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #3b3e47;
}
.register-results h3 {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 600;
}
.register-table {
    background: #22252d !important;
    border: 1px solid #3b3e47;
    border-radius: 8px;
}
.register-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.register-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.register-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.register-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.register-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
    background: #f0784c;
}
.register-table ::v-deep table {
    min-width: 620px;
}
.register-table ::v-deep th,
.register-table ::v-deep td {
    font-size: 12px !important;
    white-space: nowrap;
    border-bottom-color: #3b3e47 !important;
}
.register-table ::v-deep th {
    color: #b8bbc5 !important;
}
.register-table ::v-deep tbody tr:hover {
    background: #30343e !important;
}
.register-table ::v-deep .v-data-footer {
    border-top-color: #3b3e47;
    font-size: 12px;
}
@media (max-width: 600px) {
    .register-card {
        padding: 16px;
    }
    .register-submit {
        width: 100%;
    }
}
</style>
